// javascript
import { useState, useEffect, useRef } from 'react';
import { toast } from 'react-toastify';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import GavelIcon from '@mui/icons-material/Gavel';
import DescriptionIcon from '@mui/icons-material/Description';
import BusinessIcon from '@mui/icons-material/Business';
import AssignmentIcon from '@mui/icons-material/Assignment';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import PlaceIcon from '@mui/icons-material/Place';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import cambioEstadoService from '../services/cambioEstadoService';
import EventoService from '../services/eventoService';
import DatosMaestrosService from '../services/datosMaestrosService';
import ComboboxDatosMaestros from '../components/ComboboxDatosMaestros';

// Función auxiliar para obtener la fecha actual en formato YYYY-MM-DD
const obtenerFechaActual = () => {
    const hoy = new Date();
    return hoy.toISOString().split('T')[0];
};

function GestorEstadoEsperaFechaPericia({ tramite, onEstadoCambiado }) {
    const [cargando, setCargando] = useState(false);
    const [historialEstados, setHistorialEstados] = useState([]);
    const [cargandoHistorial, setCargandoHistorial] = useState(true);
    const [infoJudicial, setInfoJudicial] = useState(null);
    const [infoPresentacion, setInfoPresentacion] = useState(null);
    const [mostrarFormularioPericia, setMostrarFormularioPericia] = useState(false);
    const [lugares, setLugares] = useState([]);

    // Estados para el formulario de fecha de pericia
    const [datosPericia, setDatosPericia] = useState({
        fecha: obtenerFechaActual(),
        hora: '',
        lugar: '', // Sede de la pericia
        observaciones: ''
    });

    useEffect(() => {
        cargarHistorial();
        cargarLugares();
    }, [tramite.idTramite]);

    const cargarLugares = async () => {
        try {
            const response = await DatosMaestrosService.getLugares();
            setLugares(response.data);
        } catch (error) {
            console.error('Error al cargar lugares:', error);
        }
    };

    // Función para crear lugar automáticamente si no existe
    const crearLugarSiNoExiste = async (nombreLugar, tipoLugar = 'Sala') => {
        if (!nombreLugar || nombreLugar.trim() === '') return;

        const nombreTrim = nombreLugar.trim();
        
        // Verificar si ya existe
        const lugarExistente = lugares.find(l => 
            l.nombre && l.nombre.toLowerCase() === nombreTrim.toLowerCase()
        );
        
        if (lugarExistente) {
            return lugarExistente; // Ya existe
        }

        try {
            const nuevoLugar = {
                nombre: nombreTrim,
                tipo: tipoLugar,
                direccion: '',
                telefono: '',
                descripcion: `Creado automáticamente desde trámite`
            };
            const response = await DatosMaestrosService.createLugar(nuevoLugar);
            console.log('✅ Lugar creado automáticamente:', response.data);
            
            // Recargar lugares para incluir el nuevo
            const lugaresActualizados = await DatosMaestrosService.getLugares();
            setLugares(lugaresActualizados.data);
            
            return response.data;
        } catch (error) {
            console.error('Error al crear lugar automáticamente:', error);
            return null;
        }
    };

    const cargarHistorial = async () => {
        setCargandoHistorial(true);
        try {
            const historial = await cambioEstadoService.getHistorialEstados(tramite.idTramite);
            setHistorialEstados(historial);

            // 1. Buscar info judicial (Tribunal, N° Exp) de la Segunda Apelación
            const cambioSegundaApelacion = historial
                .filter(c => c.comentario && c.comentario.includes('Segunda Apelación (Demanda Judicial) registrada'))
                .pop();

            if (cambioSegundaApelacion && cambioSegundaApelacion.comentario) {
                parsearInfoJudicial(cambioSegundaApelacion.comentario);
            }

            // 2. Buscar info de la presentación de documentación (Trámite en Juicio)
            const cambioPresentacion = historial
                .filter(c => c.comentario && c.comentario.includes('Presentación de documentación registrada'))
                .pop();

            if (cambioPresentacion && cambioPresentacion.comentario) {
                parsearInfoPresentacion(cambioPresentacion.comentario);
            }

        } catch (error) {
            console.error("Error al cargar historial:", error);
            toast.error("No se pudo cargar la información del trámite judicial.");
        } finally {
            setCargandoHistorial(false);
        }
    };

    const parsearInfoJudicial = (comentario) => {
        const info = {
            numeroExpediente: null,
            juzgado: null,
            secretaria: null
        };

        const expedienteMatch = comentario.match(/Número de expediente judicial \(CSS\): (.+?)(?:\n|$)/);
        if (expedienteMatch) info.numeroExpediente = expedienteMatch[1].trim();

        const juzgadoMatch = comentario.match(/Juzgado: (.+?)(?:\n|$)/);
        if (juzgadoMatch) info.juzgado = juzgadoMatch[1].trim();

        const secretariaMatch = comentario.match(/Secretaría: (.+?)(?:\n|$)/);
        if (secretariaMatch) info.secretaria = secretariaMatch[1].trim();

        setInfoJudicial(info);
    };

    const parsearInfoPresentacion = (comentario) => {
        const info = {
            fechaPresentacion: null,
            descripcion: null
        };

        const fechaMatch = comentario.match(/Fecha de presentación: (.+?)(?:\n|$)/);
        if (fechaMatch) info.fechaPresentacion = fechaMatch[1].trim();

        const descMatch = comentario.match(/Descripción: (.+?)(?:\n|$)/);
        if (descMatch) info.descripcion = descMatch[1].trim();

        setInfoPresentacion(info);
    };

    const formatearFecha = (fechaString) => {
        if (!fechaString) return '';
        try {
            const fecha = new Date(fechaString);
            if (isNaN(fecha.getTime())) return fechaString;
            const fechaLocal = new Date(fecha.valueOf() + fecha.getTimezoneOffset() * 60000);
            return fechaLocal.toLocaleDateString('es-ES', {
                weekday: 'long',
                day: 'numeric',
                month: 'long',
                year: 'numeric'
            });
        } catch (e) {
            return fechaString;
        }
    };

    const calcularHoraFin = (horaInicio, duracionMinutos = 60) => {
        if (!horaInicio) return '';
        const [horas, minutos] = horaInicio.split(':').map(Number);
        const fechaInicio = new Date();
        fechaInicio.setHours(horas, minutos, 0, 0);
        fechaInicio.setMinutes(fechaInicio.getMinutes() + duracionMinutos);
        return fechaInicio.toTimeString().slice(0, 5);
    };

    const handleCargarFechaPericia = async () => {
        // Validaciones
        if (!datosPericia.fecha) {
            toast.error("La fecha de la pericia es obligatoria.");
            return;
        }
        if (!datosPericia.hora) {
            toast.error("La hora de la pericia es obligatoria.");
            return;
        }
        if (!datosPericia.lugar.trim()) {
            toast.error("La sede o lugar de la pericia es obligatorio.");
            return;
        }

        setCargando(true);
        try {
            // Crear lugar automáticamente si no existe
            if (datosPericia.lugar && datosPericia.lugar.trim() !== '') {
                await crearLugarSiNoExiste(datosPericia.lugar, 'Sala');
            }

            // 1. Crear evento en el calendario
            const eventoData = {
                titulo: `Pericia Médica Judicial - ${tramite.cliente?.nombre || ''} ${tramite.cliente?.apellido || ''}`,
                descripcion: `Sede/Lugar: ${datosPericia.lugar}\nObservaciones: ${datosPericia.observaciones || 'Sin observaciones'}`,
                fecha: datosPericia.fecha,
                horaInicio: datosPericia.hora,
                horaFin: calcularHoraFin(datosPericia.hora),
                todoElDia: false,
                // Su propio tipo (V254): como "Junta Médica" el recordatorio le decía al cliente
                // "tu junta médica" y la pantalla de pericia asignada no lo encontraba.
                tipoEvento: 'Pericia Médica Judicial',
                idTramite: tramite.idTramite,
                visibilidad: 'POR_AREA',
                canalesNotificacion: {
                    whatsapp: false,
                    email: false,
                    inapp: true
                }
            };

            await EventoService.crearEvento(eventoData);

            // 2. Cambiar estado del trámite a "Pericia Asignada" (Asumimos ID 17)
            const comentario = `Pericia Médica Judicial Asignada\nFecha: ${formatearFecha(datosPericia.fecha)}\nHora: ${datosPericia.hora}\nSede/Lugar: ${datosPericia.lugar}\nObservaciones: ${datosPericia.observaciones || 'Sin observaciones'}`;

            const cambioEstadoData = {
                idEstado: 17, // Siguiente estado: PERICIA ASIGNADA
                comentario: comentario
            };

            await cambioEstadoService.cambiarEstado(tramite.idTramite, cambioEstadoData);

            toast.success("Fecha de pericia cargada y evento creado correctamente.");

            // Limpiar formulario
            setMostrarFormularioPericia(false);
            setDatosPericia({ fecha: obtenerFechaActual(), hora: '', lugar: '', observaciones: '' });

            if (onEstadoCambiado) {
                await onEstadoCambiado();
            }

        } catch (error) {
            console.error("Error al cargar fecha de pericia:", error);
            toast.error("Error al registrar la fecha de pericia: " + (error.response?.data || error.message));
        } finally {
            setCargando(false);
        }
    };

    if (cargandoHistorial) {
        return (
            <div className="card shadow-lg border-0 rounded-4 p-4 mb-4">
                <div className="alert alert-info">Cargando información...</div>
            </div>
        );
    }

    return (
        <div className="card shadow-lg border-0 rounded-4 p-4 mb-4">
            <div className="d-flex justify-content-between align-items-center mb-3">
                <h4 className="fw-bold text-info mb-0">
                    <HourglassEmptyIcon className="me-2" />
                    Espera Fecha de Pericia
                </h4>
                <span className="badge bg-info rounded-pill px-3 py-2">
                    <AccessTimeIcon fontSize="small" className="me-1" />
                    Pendiente de Fecha
                </span>
            </div>

            <div className="alert alert-info border-0 rounded-3">
                <strong>Siguiente Paso:</strong>
                <p className="mb-0 mt-1">
                    Se presentó toda la documentación. Se está a la espera de la notificación del tribunal con la fecha de la pericia médica judicial.
                </p>
            </div>

            {/* Información Judicial y de Presentación */}
            {(infoJudicial || infoPresentacion) && (
                <div className="mt-3 p-3 bg-light rounded-3 border mb-4">
                    <h6 className="fw-semibold text-secondary mb-3">Resumen Judicial</h6>
                    <div className="row g-3">
                        {infoJudicial?.juzgado && (
                            <div className="col-md-6 d-flex align-items-center">
                                <BusinessIcon className="text-primary me-2" />
                                <div>
                                    <small className="text-muted d-block">Tribunal/Juzgado</small>
                                    <strong className="text-dark">{infoJudicial.juzgado}</strong>
                                </div>
                            </div>
                        )}
                        {infoJudicial?.numeroExpediente && (
                            <div className="col-md-6 d-flex align-items-center">
                                <DescriptionIcon className="text-primary me-2" />
                                <div>
                                    <small className="text-muted d-block">N° Expediente Judicial</small>
                                    <strong className="text-dark">{infoJudicial.numeroExpediente}</strong>
                                </div>
                            </div>
                        )}
                        {infoPresentacion?.fechaPresentacion && (
                            <div className="col-md-6 d-flex align-items-center">
                                <CalendarMonthIcon className="text-primary me-2" />
                                <div>
                                    <small className="text-muted d-block">Fecha Presentación Documentación</small>
                                    <strong className="text-dark">{infoPresentacion.fechaPresentacion}</strong>
                                </div>
                            </div>
                        )}
                        {infoPresentacion?.descripcion && (
                            <div className="col-md-6 d-flex align-items-center">
                                <AssignmentIcon className="text-primary me-2" />
                                <div>
                                    <small className="text-muted d-block">Documentación Presentada</small>
                                    <strong className="text-dark">{infoPresentacion.descripcion}</strong>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* Formulario para Cargar Fecha de Pericia */}
            {mostrarFormularioPericia ? (
                <div className="mt-4">
                    <div className="card shadow-sm border-0 rounded-4 p-4">
                        <h5 className="fw-semibold text-primary mb-3">Cargar Fecha de Pericia Médica</h5>
                        <div className="row g-3">
                            <div className="col-md-6">
                                <label className="form-label fw-semibold">
                                    Fecha de Pericia <span className="text-danger">*</span>
                                </label>
                                <div className="input-group">
                                    <span className="input-group-text bg-light"><CalendarMonthIcon fontSize="small" /></span>
                                    <input
                                        type="date"
                                        className="form-control"
                                        value={datosPericia.fecha}
                                        onChange={e => setDatosPericia({ ...datosPericia, fecha: e.target.value })}
                                        required
                                    />
                                </div>
                            </div>
                            <div className="col-md-6">
                                <label className="form-label fw-semibold">
                                    Hora <span className="text-danger">*</span>
                                </label>
                                <div className="input-group">
                                    <span className="input-group-text bg-light"><AccessTimeIcon fontSize="small" /></span>
                                    <input
                                        type="time"
                                        className="form-control"
                                        value={datosPericia.hora}
                                        onChange={e => setDatosPericia({ ...datosPericia, hora: e.target.value })}
                                        required
                                    />
                                </div>
                            </div>
                            <div className="col-12">
                                <ComboboxDatosMaestros
                                    opciones={lugares.filter(l => ['Sala', 'Dependencia', 'Tribunal'].includes(l.tipo) || !l.tipo)}
                                    valor={datosPericia.lugar}
                                    onChange={(valor) => setDatosPericia({ ...datosPericia, lugar: valor })}
                                    tipo="lugar"
                                    tipoLugar="Sala"
                                    placeholder="Seleccione o escriba un lugar..."
                                    label="Sede / Lugar"
                                    required={true}
                                    icon={PlaceIcon}
                                />
                                <small className="text-muted d-block mt-1">Lugar donde se realizará la pericia.</small>
                            </div>
                            <div className="col-12">
                                <label className="form-label fw-semibold">Observaciones</label>
                                <textarea
                                    className="form-control"
                                    rows="2"
                                    value={datosPericia.observaciones}
                                    onChange={e => setDatosPericia({ ...datosPericia, observaciones: e.target.value })}
                                    placeholder="Indicaciones adicionales, perito asignado, etc."
                                />
                            </div>
                        </div>

                        <div className="d-flex justify-content-center gap-3 mt-4">
                            <button
                                className="btn btn-primary d-flex align-items-center gap-2 rounded-pill px-4 py-2 fw-semibold"
                                onClick={handleCargarFechaPericia}
                                disabled={cargando}
                            >
                                <CheckCircleIcon fontSize="small" />
                                {cargando ? 'Guardando...' : 'Confirmar Fecha y Notificar'}
                            </button>
                            <button
                                className="btn btn-outline-secondary d-flex align-items-center gap-2 rounded-pill px-4 py-2 fw-semibold"
                                onClick={() => setMostrarFormularioPericia(false)}
                                disabled={cargando}
                            >
                                <CancelIcon fontSize="small" />
                                Cancelar
                            </button>
                        </div>
                    </div>
                </div>
            ) : (
                <div className="d-flex justify-content-center flex-wrap gap-3 mt-4 pt-3 border-top">
                    <button
                        className="btn btn-primary d-flex align-items-center gap-2 rounded-pill px-4 py-2 fw-semibold"
                        onClick={() => setMostrarFormularioPericia(true)}
                        disabled={cargando}
                    >
                        <UploadFileIcon fontSize="small" />
                        Cargar Fecha de Pericia
                    </button>
                </div>
            )}
        </div>
    );
}

export default GestorEstadoEsperaFechaPericia;